import { Check, Download, Eye, FilePlus2, MoreVertical, RotateCcw, Trash2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { confirmModal, promptModal } from '@/components/ui/modal'
import { RequestDocumentModal } from '@/features/hr/belgeler/components/RequestDocumentModal'
import { DOC_FOLLOWUP_PERM, DOC_REVIEW_PERM, downloadCaseFile, type ReviewDecision, useReviewCase } from '@/features/hr/belgeler/followupApi'
import { fmtDate } from '@/features/shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useSubmitDocumentCase } from '@/features/hr/belgeler/api'
import { downloadPersonnelDocument, useArchivePersonnelDocument, useUploadPersonnelDocument } from '../../api'
import type { PersonelDetail } from '../../model'
import { printDoc } from '../../lib/exportActions'
import { HR_DOC_CASE_WRITE_PERM, HR_DOCUMENTS_WRITE_PERM } from '../../../permissions'
import { apiErrorMessage } from '@/i18n'

type Belge = PersonelDetail['belge']['liste'][number]

export function BelgeRowActions({ personelId, personelAd, belge }: { personelId: string; personelAd: string; belge: Belge }) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const eksik = belge.durum === 'eksik'
  const { can } = useAuth()
  const submitCase = useSubmitDocumentCase()
  const upload = useUploadPersonnelDocument()
  const archive = useArchivePersonnelDocument()
  const ozluk = belge.kaynak === 'ozluk' || belge.kaynak === 'tur'
  // Şablon gereksinimi için henüz talep açılmamıştır: dosya yüklenecek bir hedef yoktur.
  const canWrite = belge.kaynak === 'gereksinim' ? false : ozluk ? can(HR_DOCUMENTS_WRITE_PERM) : can(HR_DOC_CASE_WRITE_PERM)
  const fail = (error: unknown) => toast.error(apiErrorMessage(error))
  const review = useReviewCase()
  const [requestOpen, setRequestOpen] = useState(false)
  const isCase = belge.kaynak === 'case'
  const hasFile = isCase && ['awaiting_review', 'completed', 'correction_requested'].includes(belge.caseStatus ?? '')
  const canReview = isCase && belge.caseStatus === 'awaiting_review' && can(DOC_REVIEW_PERM)
  // Eksik gereksinim için talep açılmamışsa takip yetkilisi talep açabilir (önceden bu satırda işlem yoktu).
  const canRequest = belge.kaynak === 'gereksinim' && !!belge.templateId && can(DOC_FOLLOWUP_PERM)
  const decide = async (decision: ReviewDecision) => {
    let reason: string | undefined
    if (decision !== 'approved') {
      const v = await promptModal({ title: t(`hr.belgeler.review.${decision}Title`), placeholder: t('hr.belgeler.review.reasonPlaceholder'), confirmLabel: t(`hr.belgeler.review.${decision}`) })
      if (v == null) return
      if (!v.trim()) {
        toast.error(t('hr.belgeler.review.reasonRequired'))
        return
      }
      reason = v.trim()
    }
    review.mutate({ caseId: belge.id, employeeId: personelId, decision, reason }, { onSuccess: () => toast.success(t(`hr.belgeler.review.${decision}Done`)), onError: fail })
  }
  const durumLabel = t(`hr.personel.belge.durum.${belge.durum}`)

  const view = () => {
    // Yüklenmiş özlük belgesi gerçek dosya olarak indirilir; diğerleri için kayıt özeti yazdırılır.
    if (belge.kaynak === 'ozluk') {
      void downloadPersonnelDocument(personelId, belge.id, belge.dosyaAdi || belge.ad).catch(fail)
      return
    }
    if (hasFile) {
      void downloadCaseFile(belge.id, belge.ad).catch(fail)
      return
    }
    printDoc({
      title: belge.ad,
      subtitle: `${personelAd} · ${belge.kategori}`,
      sections: [
        {
          heading: t('hr.personel.belge.title'),
          rows: [
            [t('hr.personel.belge.klasor'), belge.kategori],
            [t('hr.personel.belge.boyut'), `${belge.tur} · ${belge.boyut}`],
            [t('hr.personel.belge.tarih'), belge.tarih === '—' ? '—' : fmtDate(belge.tarih)],
            [t('hr.personel.belge.gecerlilik'), belge.gecerlilik === 'Süresiz' ? t('hr.personel.belge.suresiz') : belge.gecerlilik],
            [t('hr.personel.columns.durum'), durumLabel],
          ],
        },
      ],
    })
  }

  const onFile = (f: File) => {
    const done = () => toast.success(t(eksik ? 'hr.personel.actions.belge.uploaded' : 'hr.personel.actions.belge.replaced', { name: belge.ad }))
    if (ozluk && belge.kindKey) {
      upload.mutate({ employeeId: personelId, kind: belge.kindKey, file: f }, { onSuccess: done, onError: fail })
      return
    }
    submitCase.mutate({ caseId: belge.id, employeeId: personelId, file: f }, { onSuccess: done, onError: fail })
  }

  const remove = async () => {
    const ok = await confirmModal({
      title: t('confirm.belgeDelete.title'),
      description: t('confirm.belgeDelete.body', { name: belge.ad }),
      confirmLabel: t('hr.personel.actions.belge.remove'),
      destructive: true,
    })
    if (!ok) return
    archive.mutate(
      { employeeId: personelId, documentId: belge.id },
      { onSuccess: () => toast.success(t('hr.personel.actions.belge.removed', { name: belge.ad })), onError: fail },
    )
  }

  if (eksik && !canWrite && !canRequest) return null

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          e.target.value = ''
        }}
      />
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
            aria-label={t('hr.personel.columns.islem')}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 rounded-xl" onClick={(e) => e.stopPropagation()}>
          {!eksik || hasFile ? (
            <DropdownMenuItem onSelect={view}>
              {hasFile ? <Download /> : <Eye />}
              {t(hasFile ? 'hr.belgeler.review.download' : 'hr.personel.actions.belge.view')}
            </DropdownMenuItem>
          ) : null}
          {canReview ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void decide('approved')}>
                <Check />
                {t('hr.belgeler.review.approved')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void decide('correction_requested')}>
                <RotateCcw />
                {t('hr.belgeler.review.correction_requested')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void decide('rejected')} className="text-destructive data-[highlighted]:text-destructive [&_svg]:text-destructive">
                <X />
                {t('hr.belgeler.review.rejected')}
              </DropdownMenuItem>
            </>
          ) : null}
          {canRequest ? (
            <DropdownMenuItem onSelect={() => setRequestOpen(true)}>
              <FilePlus2 />
              {t('hr.belgeler.request.openCase')}
            </DropdownMenuItem>
          ) : null}
          {canWrite && !(isCase && !['open', 'correction_requested'].includes(belge.caseStatus ?? 'open')) ? (
            <>
              <DropdownMenuItem onSelect={() => input.current?.click()}>
                <Upload />
                {t(eksik ? 'hr.personel.actions.belge.upload' : 'hr.personel.actions.belge.replace')}
              </DropdownMenuItem>
            </>
          ) : null}
          {canWrite && belge.kaynak === 'ozluk' ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => void remove()}
                className="text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive [&_svg]:text-destructive"
              >
                <Trash2 />
                {t('hr.personel.actions.belge.remove')}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {requestOpen ? <RequestDocumentModal employeeIds={[personelId]} templateId={belge.templateId} onDone={() => setRequestOpen(false)} /> : null}
    </>
  )
}
